<template>
  <div
    class="routing-status"
    :class="{ mobileOfRoutingStatus: !isExtraLargeScreen, mobile: isMobile }"
  >
    <div class="subtitle-container">
      <h6>Routing status for {{ title }}</h6>
      <fa
        v-if="!isExpandLegend && !isMobile"
        icon="question-circle"
        v-tooltip.top="{ content: 'Legend' }"
        @click="isExpandLegend = true"
      ></fa>
      <fa
        v-if="isExpandLegend && !isMobile"
        icon="chevron-up"
        @click="isExpandLegend = false"
      ></fa>
    </div>

    <div
      v-if="isMobile"
      class="legend-toggle"
      @click="isExpandLegend = !isExpandLegend"
    >
      <p>Legend</p>
      <div class="icon-wrapper">
        <fa
          icon="chevron-down"
          :class="{ 'fa-rotate-180': isExpandLegend }"
        ></fa>
      </div>
    </div>

    <div class="legend-content" :class="{ show: isExpandLegend }">
      <h5 v-if="!isMobile">Legend</h5>
      <p>
        For all prefixes seen in BGP, the system analyses this BGP against RPKI
        ROA and IRR route object status. The status can be:
      </p>
      <p class="prefix-icon green-dot">
        Published
      </p>
      <p class="description">Record of this type does not conflict with BGP.</p>
      <p class="prefix-icon yellow-dot">
        Not published
      </p>
      <p class="description">
        No record of this type applies to this BGP announcement.
      </p>
      <p class="prefix-icon red-dot">
        Mismatch
      </p>
      <p class="description">
        Published record of this type conflicts with this route seen in BGP. If
        your prefix has this label, click on its '+info' link for more details.
      </p>
    </div>

    <div v-if="isShowIndication" class="indicate-msg">
      <span>*</span><span>indicate prefixes I am NOT the holder of</span>
    </div>

    <div v-if="isExtraLargeScreen">
      <div class="tools">
        <div class="tools__first-row">
          <div class="entries">
            <Selector
              width="9rem"
              prependText="Show"
              :optionList="numberOfEntriesOptionList"
              :value="numberOfEntriesPerPage"
              @onValueChange="setNumberOfEntriesPerPage"
            ></Selector>
          </div>
          <div class="search">
            <Search
              placeholder="Search by prefix or ASN"
              width="15rem"
              :isWithIcon="true"
              :isSearchingInRealTime="true"
              :defaultQuery="query"
              @onSearch="handleSearch"
              ref="search"
            ></Search>
          </div>
        </div>
        <div class="tools__second-row">
          <div class="filter">
            <Checkbox
              prependText="Filter by:"
              :checkboxList="filterList"
              :defaultSelectedList="filter"
              @onValueChange="setFilter"
              ref="filter"
            ></Checkbox>
          </div>
          <div
            @click="downloadRoutingStatusData"
            class="download-button"
            v-if="routingStatusListBySort.length"
            v-tooltip.left="{
              content: 'Export routing status data as a CSV file.',
              classes: 'routing-status-download-tooltip'
            }"
          >
            <i class="icon-download" aria-hidden="true"></i>
          </div>
        </div>
      </div>
      <div class="table">
        <RoutingStatusTable
          :routingStatusList="routingStatusListByPagination"
          @onSetSort="setSort"
          :defaultSort="{ field: this.orderField, order: this.orderType }"
        ></RoutingStatusTable>
      </div>
      <div v-if="routingStatusListBySort.length" class="pagination-section">
        <Pagination
          :currentPage="currentPage"
          :listLength="routingStatusListBySort.length"
          :pageSize="numberOfEntriesPerPage"
          @onSetPage="setPage"
        ></Pagination>
      </div>
      <MismatchInfoModal v-if="isShowMismatchModal"></MismatchInfoModal>
      <TruncatedResponseModal
        v-if="isTruncatedModalShown"
        @closeModal="closeTruncatedResponseModal"
        :truncatedPrefixNumber="truncatedPrefixNumber"
      ></TruncatedResponseModal>
    </div>

    <!-- Mobile design -->
    <div v-else :class="{ mobileOfRoutingStatus: !isExtraLargeScreen }">
      <div class="tools">
        <div class="tools__left">
          <fa
            icon="sort-amount-down"
            class="icon"
            :class="{ active: isShowSortTool }"
            @click="toggleIsShowSortTool"
          ></fa>
          <fa
            icon="filter"
            class="icon"
            :class="{ active: isShowFilterTool }"
            @click="toggleIsShowFilterTool"
          ></fa>
          <div
            @click="downloadRoutingStatusData"
            class="download-button"
            v-tooltip.left="{
              content: 'Export routing status data as a CSV file.',
              classes: 'routing-status-download-tooltip'
            }"
          >
            <i class="icon-download" aria-hidden="true"></i>
          </div>
        </div>
        <div class="tools__right">
          <Search
            placeholder="Search by prefix or ASN"
            width="14.5rem"
            :isWithIcon="true"
            :isSearchingInRealTime="true"
            :defaultQuery="query"
            @onSearch="handleSearch"
            ref="search"
          ></Search>
        </div>
      </div>
      <div>
        <div v-if="isShowSortTool" class="sort-tool">
          <SortAndFilterTool
            :toolConfig="sortToolConfig"
            @onToolChange="handleSortToolChange"
          ></SortAndFilterTool>
        </div>
        <div v-if="isShowFilterTool" class="filter-tool">
          <!-- <SortAndFilterTool
            :toolConfig="filterToolConfig"
            @onToolChange="handleFilterToolChange"
          ></SortAndFilterTool> -->
          <div class="filter">
            <Checkbox
              prependText="Filter by:"
              :checkboxList="filterList"
              :defaultSelectedList="filter"
              @onValueChange="setFilter"
              ref="filter"
            ></Checkbox>
          </div>
        </div>
      </div>
      <div v-if="routingStatusListByPagination.length" class="card-list">
        <div
          v-for="(item, index) in routingStatusListByPagination"
          :key="index"
          class="card-wrapper"
        >
          <RoutingStatusCard :routingStatusData="item"></RoutingStatusCard>
        </div>
      </div>
      <div v-else>
        <div class="no-data">No data</div>
      </div>
      <div v-if="routingStatusListBySort.length" class="pagination-section">
        <Pagination
          :currentPage="currentPage"
          :listLength="routingStatusListBySort.length"
          :pageSize="numberOfEntriesPerPage"
          @onSetPage="setPage"
        ></Pagination>
      </div>
      <MismatchInfoModal v-if="isShowMismatchModal"></MismatchInfoModal>
      <TruncatedResponseModal
        v-if="isTruncatedModalShown"
        @closeModal="closeTruncatedResponseModal"
        :truncatedPrefixNumber="truncatedPrefixNumber"
      ></TruncatedResponseModal>
    </div>
  </div>
</template>

<script>
import Search from '../ui/Search.vue';
import Selector from '../ui/Selector.vue';
import Checkbox from '../ui/Checkbox.vue';
import Pagination from '../ui/Pagination.vue';
import RoutingStatusTable from './RoutingStatusTable.vue';
import MismatchInfoModal from './MismatchInfoModal.vue';
import {
  CONTEXT_TYPE,
  ROA_STATUS,
  ROUTE_OBJECT_STATUS,
  ROUTING_STATUS_SORT_OPTION,
  ROUTING_STATUS_FILTER_OPTION,
  ORDER_TYPE
} from '../../constants/routingStatusConstant';
import { formatRoutes, sortRoutes } from '../../utils/responseFormatter';
import SortAndFilterTool from '../../components/alerts/overview/alertTableParts/SortAndFilterTool.vue';
import RoutingStatusCard from './RoutingStatusCard.vue';
import TruncatedResponseModal from './TruncatedResponseModal.vue';

import { mapGetters, mapMutations, mapState } from 'vuex';
import { saveAs } from 'file-saver';

export default {
  name: 'RoutingStatusSection',
  components: {
    Search,
    Selector,
    Checkbox,
    Pagination,
    RoutingStatusTable,
    MismatchInfoModal,
    SortAndFilterTool,
    RoutingStatusCard,
    TruncatedResponseModal
  },
  data() {
    return {
      isExpandLegend: false,
      query: '',
      numberOfEntriesPerPage: 20, // It must be one of the "value" in numberOfEntriesOptionList
      numberOfEntriesOptionList: [
        {
          name: '20 entries',
          value: 20
        },
        {
          name: '50 entries',
          value: 50
        },
        {
          name: '100 entries',
          value: 100
        }
      ],
      filter: [],
      orderField: '',
      orderType: '',
      isShowSortTool: false,
      isShowFilterTool: false,
      sortToolConfig: {
        placeholder: 'Sort',
        toolList: [
          { id: ROUTING_STATUS_SORT_OPTION.NONE, text: 'None' },
          {
            id: ROUTING_STATUS_SORT_OPTION.PREFIX_ASCENDING,
            text: 'Prefix ascending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.PREFIX_DESCENDING,
            text: 'Prefix descending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.BGP_ROUTE_ASCENDING,
            text: 'BGP route ascending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.BGP_ROUTE_DESCENDING,
            text: 'BGP route descending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ORIGIN_AS_ASCENDING,
            text: 'Origin AS ascending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ORIGIN_AS_DESCENDING,
            text: 'Origin AS descending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ROA_ASCENDING,
            text: 'ROA status ascending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ROA_DESCENDING,
            text: 'ROA status descending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ROUTE_OBJECT_ASCENDING,
            text: 'Route object status ascending'
          },
          {
            id: ROUTING_STATUS_SORT_OPTION.ROUTE_OBJECT_DESCENDING,
            text: 'Route object status descending'
          }
        ]
      },
      isTruncatedModalShown: false
    };
  },
  watch: {
    routingStatusOfSelectedContext() {
      // Reset filter and search when context is changed
      if (this.$refs.search) this.$refs.search.searchQuery = '';
      if (this.$refs.filter) this.$refs.filter.selectedList = [];
      if (this.isShowSortTool) this.toggleIsShowSortTool();
      if (this.isShowFilterTool) this.toggleIsShowFilterTool();
    },
    filter(current, prev) {
      if (current.length !== 0) this.isShowFilterTool = true;
    },
    truncatedPrefixNumber(currentVal) {
      if (currentVal) this.isTruncatedModalShown = true;
    }
  },
  computed: {
    ...mapState('routingStatusStore', [
      'contextType',
      'selectedASN',
      'currentPage',
      'isShowMismatchModal'
    ]),
    ...mapGetters('routingStatusStore', ['routingStatusOfSelectedContext']),
    ...mapGetters(['isExtraLargeScreen', 'isMobile']),

    title() {
      if (this.contextType === CONTEXT_TYPE.PREFIXES) {
        return 'your prefixes';
      } else {
        return `AS${this.selectedASN}`;
      }
    },
    filterList() {
      if (this.contextType === CONTEXT_TYPE.PREFIXES) {
        return [
          {
            value: 'roa',
            label: 'ROA issues'
          },
          {
            value: 'routeObject',
            label: 'Route object issues'
          }
        ];
      } else {
        return [
          {
            value: 'roa',
            label: 'ROA issues'
          },
          {
            value: 'routeObject',
            label: 'Route object issues'
          },
          {
            value: `currentASN`,
            label: `Only ${this.title}`
          }
        ];
      }
    },
    filterToolConfig() {
      if (this.contextType === CONTEXT_TYPE.PREFIXES) {
        return {
          placeholder: 'Filter',
          toolList: [
            { id: ROUTING_STATUS_FILTER_OPTION.NONE, text: 'None' },
            {
              id: ROUTING_STATUS_FILTER_OPTION.ROA_ISSUE,
              text: 'ROA issues'
            },
            {
              id: ROUTING_STATUS_FILTER_OPTION.ROUTE_OBJECT_ISSUE,
              text: 'Route object issues'
            }
          ]
        };
      } else {
        return {
          placeholder: 'Filter',
          toolList: [
            { id: ROUTING_STATUS_FILTER_OPTION.NONE, text: 'None' },
            {
              id: ROUTING_STATUS_FILTER_OPTION.ROA_ISSUE,
              text: 'ROA issues'
            },
            {
              id: ROUTING_STATUS_FILTER_OPTION.ROUTE_OBJECT_ISSUE,
              text: 'Route object issues'
            },
            {
              id: ROUTING_STATUS_FILTER_OPTION.ONLY_CURRENT_ASN,
              text: `Only ${this.title}`
            }
          ]
        };
      }
    },
    isShowIndication() {
      return this.contextType === CONTEXT_TYPE.ASN;
    },
    routingStatusList() {
      return this.routingStatusOfSelectedContext
        ? this.routingStatusOfSelectedContext.routes || []
        : [];
    },
    myPrefixesList() {
      return this.routingStatusOfSelectedContext
        ? this.routingStatusOfSelectedContext.my_prefixes || []
        : [];
    },
    truncatedPrefixNumber() {
      return this.routingStatusOfSelectedContext
        ? this.routingStatusOfSelectedContext.truncated_to_num_bgp_prefixes || 0
        : 0;
    },
    routingStatusListWithMyPrefixes() {
      const isForMyPrefixes = this.contextType === CONTEXT_TYPE.PREFIXES;

      return formatRoutes(
        this.routingStatusList,
        this.myPrefixesList,
        isForMyPrefixes
      );
    },
    routingStatusListByQuery() {
      if (!this.query) return this.routingStatusListWithMyPrefixes;

      return this.routingStatusListWithMyPrefixes.filter(routingStatus => {
        if (routingStatus.route.prefix.includes(this.query)) return true;

        if (('as' + routingStatus.route.asn).includes(this.query)) return true;

        return false;
      });
    },
    routingStatusListByFilter() {
      if (this.filter.length === 0) return this.routingStatusListByQuery;

      let routingStatusFilteredList = [...this.routingStatusListByQuery];

      if (this.filter.includes('currentASN'))
        routingStatusFilteredList = routingStatusFilteredList.filter(
          routingStatus => routingStatus.route.asn === Number(this.selectedASN)
        );

      // Select "ROA issues" but not "Route object issues"
      if (this.filter.includes('roa') && !this.filter.includes('routeObject'))
        routingStatusFilteredList = routingStatusFilteredList.filter(
          routingStatus => routingStatus.roa_status === ROA_STATUS.MISMATCH
        );

      // Select "Route object issues" but not "ROA issues"
      if (!this.filter.includes('roa') && this.filter.includes('routeObject'))
        routingStatusFilteredList = routingStatusFilteredList.filter(
          routingStatus =>
            routingStatus.route_object_status === ROUTE_OBJECT_STATUS.MISMATCH
        );

      // Select both "Route object issues" and "ROA issues"
      if (this.filter.includes('roa') && this.filter.includes('routeObject'))
        routingStatusFilteredList = routingStatusFilteredList.filter(
          routingStatus =>
            routingStatus.roa_status === ROA_STATUS.MISMATCH ||
            routingStatus.route_object_status === ROUTE_OBJECT_STATUS.MISMATCH
        );

      return routingStatusFilteredList;

      /*
      if(this.filter.includes('roa'))
      const routingStatusWithIssuesList = this.routingStatusListByQuery.filter(
        routingStatus => {
          if (
            this.filter.includes('roa') &&
            routingStatus.roa_status === ROA_STATUS.MISMATCH
          )
            return true;

          if (
            this.filter.includes('routeObject') &&
            routingStatus.route_object_status === ROUTE_OBJECT_STATUS.MISMATCH
          )
            return true;

          if (
            this.filter.includes('currentASN') &&
            routingStatus.route.asn === Number(this.selectedASN)
          )
            return true;

          return true;
        }
      );
      */
    },
    routingStatusListBySort() {
      return sortRoutes(this.routingStatusListByFilter, {
        orderField: this.orderField,
        orderType: this.orderType
      });
    },
    routingStatusListByPagination() {
      const start = (this.currentPage - 1) * this.numberOfEntriesPerPage;
      const end = this.currentPage * this.numberOfEntriesPerPage;
      return this.routingStatusListBySort.slice(start, end);
    }
  },
  methods: {
    ...mapMutations('routingStatusStore', ['set_currentPage']),
    handleSearch(val) {
      this.query = val;

      this.set_currentPage(1);
    },
    setNumberOfEntriesPerPage(val) {
      this.numberOfEntriesPerPage = val;

      this.set_currentPage(1);
    },
    setFilter(val) {
      this.filter = val;

      this.set_currentPage(1);
    },
    setPage(val) {
      this.set_currentPage(val);
    },
    setSort(val) {
      const { property, order } = val;

      if (order) {
        this.orderField = property;
        this.orderType = order;
      } else {
        this.orderField = '';
        this.orderType = '';
      }
    },
    downloadRoutingStatusData() {
      let data = 'Prefix,BGP route,Origin AS,ROA,Route object\n';

      this.routingStatusListBySort.forEach(item => {
        data += `${item.route.prefix},${item.route.prefix},AS${
          item.route.asn
        },${item.roa_status},${item.route_object_status}\n`;
      });

      const blob = new Blob([data], { type: 'text/csv' });

      saveAs(blob, `routing status of ${this.selectedASN}.csv`);
    },
    toggleIsShowSortTool() {
      this.isShowSortTool = !this.isShowSortTool;
      if (!this.isShowSortTool)
        this.handleSortToolChange(ROUTING_STATUS_SORT_OPTION.NONE);
    },
    toggleIsShowFilterTool() {
      this.isShowFilterTool = !this.isShowFilterTool;
      if (!this.isShowFilterTool) this.filter = [];
    },
    handleSortToolChange(val) {
      switch (val) {
        case ROUTING_STATUS_SORT_OPTION.NONE:
          this.orderField = '';
          this.orderType = '';
          break;
        case ROUTING_STATUS_SORT_OPTION.PREFIX_ASCENDING:
          this.orderField = 'delegatedPrefix';
          this.orderType = ORDER_TYPE.ASC;
          break;
        case ROUTING_STATUS_SORT_OPTION.PREFIX_DESCENDING:
          this.orderField = 'delegatedPrefix';
          this.orderType = ORDER_TYPE.DESC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ROA_ASCENDING:
          this.orderField = 'roa_status';
          this.orderType = ORDER_TYPE.ASC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ROA_ASCENDING:
          this.orderField = 'roa_status';
          this.orderType = ORDER_TYPE.DESC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ROUTE_OBJECT_ASCENDING:
          this.orderField = 'route_object_status';
          this.orderType = ORDER_TYPE.ASC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ROUTE_OBJECT_DESCENDING:
          this.orderField = 'route_object_status';
          this.orderType = ORDER_TYPE.DESC;
          break;
        case ROUTING_STATUS_SORT_OPTION.BGP_ROUTE_ASCENDING:
          this.orderField = 'route.prefix';
          this.orderType = ORDER_TYPE.ASC;
          break;
        case ROUTING_STATUS_SORT_OPTION.BGP_ROUTE_DESCENDING:
          this.orderField = 'route.prefix';
          this.orderType = ORDER_TYPE.DESC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ORIGIN_AS_ASCENDING:
          this.orderField = 'route.asn';
          this.orderType = ORDER_TYPE.ASC;
          break;
        case ROUTING_STATUS_SORT_OPTION.ORIGIN_AS_DESCENDING:
          this.orderField = 'route.asn';
          this.orderType = ORDER_TYPE.DESC;
          break;
        default:
          this.orderField = '';
          this.orderType = '';
          break;
      }
    },
    handleFilterToolChange(val) {
      switch (val) {
        case ROUTING_STATUS_FILTER_OPTION.NONE:
          this.filter = [];
          break;
        case ROUTING_STATUS_FILTER_OPTION.ROA_ISSUE:
          this.filter = ['roa'];
          break;
        case ROUTING_STATUS_FILTER_OPTION.ROUTE_OBJECT_ISSUE:
          this.filter = ['routeObject'];
          break;
        case ROUTING_STATUS_FILTER_OPTION.ONLY_CURRENT_ASN:
          this.filter = ['currentASN'];
          break;
        default:
          this.filter = [];
          break;
      }
    },
    closeTruncatedResponseModal() {
      this.isTruncatedModalShown = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.routing-status {
  margin-top: 1rem;

  .subtitle-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    h6 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
    }

    svg {
      display: inline-block;
      margin-right: 0.5rem;
      font-size: 1.25rem;
      color: $icon-grey;
      cursor: pointer;
    }
  }

  .legend-toggle {
    display: flex;
    align-items: center;
    margin: 0;
    width: 12rem;

    p {
      margin: 0;
      border-bottom: 1px solid $text-black;
      cursor: pointer;
    }

    .icon-wrapper {
      padding: 0.5rem;
      padding-right: 1.5rem;
      cursor: pointer;

      svg {
        transition: transform 0.5s;
      }
    }
  }

  .legend-content {
    border: none;
    padding: 0;
    opacity: 0;
    height: auto;
    max-height: 0;
    overflow: hidden;
    transition-property: opacity max-height;
    transition-duration: 0.5s;

    &.show {
      opacity: 1;
      padding-top: 1rem;
      max-height: 50rem;
      border-top: 1px solid #bbb;
      border-bottom: 1px solid #bbb;
    }

    .prefix-icon {
      margin-bottom: 0.5rem;
    }

    .description {
      padding-left: 1.2rem;
    }
  }

  .indicate-msg {
    margin: 1rem 0;
  }

  .tools {
    display: flex;
    flex-direction: column;
    margin-top: 1rem;

    &__first-row {
      display: flex;
      justify-content: space-between;

      & > div {
        margin-bottom: 1rem;

        .custom-select {
          flex: 0 1 auto;
          width: 9rem;
        }
      }
    }

    &__second-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .download-button {
        padding: 0.25rem 0.5rem;
        color: $icon-grey;
        cursor: pointer;

        &:hover {
          color: $text-black;
        }

        i {
          font-size: 1.25rem;
        }
      }
    }
  }

  .table {
    margin: 1rem 0 0 0;
  }

  .pagination-section {
    background-color: #fff;
    margin-bottom: 2rem;
    border-top: 1px solid $text-grey;
    padding: 1rem 0.5rem;
  }
}

.routing-status.mobileOfRoutingStatus {
  padding-bottom: 2rem;

  .subtitle-container {
    h6 {
      font-size: 1.25rem;
    }
  }

  .tools {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      .icon {
        padding: 0.5rem;
        width: 2rem;
        height: 2rem;
        color: $icon-grey;
      }

      .active {
        color: $text-light-blue;
      }

      .download-button {
        padding: 0.25rem 0.5rem;
        color: $icon-grey;

        i {
          font-size: 1rem;
        }
      }
    }
  }
  .sort-tool,
  .filter-tool {
    padding-top: 0.5rem;
  }

  .card-list {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin: 1rem 0;

    .card-wrapper {
      flex: 0 1 49%;

      @media screen and (max-width: 992px) {
        flex: 0 1 100%;
      }
    }
  }

  .no-data {
    margin: 1rem 0;
    height: 3rem;
    line-height: 3rem;
    text-align: center;
    background: #fff;
  }

  .pagination-section {
    background-color: initial;
    margin-bottom: 2rem;
    border-top: none;
    padding: 0 0.5rem;
  }
}

.mobile {
  .legend-content.show {
    border: none;
  }
}
</style>
